import { type ToolSpec, n, s, fmt, money } from "./types";

export const HOMEYARD_TOOLS: ToolSpec[] = [
  {
    slug: "aquarium-stocking-calculator",
    cluster: "lifestyle",
    name: "Aquarium Stocking",
    h1: "Aquarium Fish Stocking & Weekly Water Change Volume Calculator",
    title: "Aquarium Stocking & Water Change Calculator (2026)",
    description:
      "Calculate your aquarium stocking capacity percentage instantly. Free online aquarium stocking calculator with live formulas, unit conversion and a step-by-step breakdown.",
    keywords: [
      "aquarium stocking calculator by gallon",
      "how much water to change weekly fish tank",
      "fish tank bioload calculator",
      "filter turnover rate calculator",
      "how many fish can i put in my tank",
      "weekly water change percentage calculator",
    ],
    inputs: [
      { id: "volume", label: "Tank volume", type: "number", def: "55", unit: "gal", min: 1, max: 10000, step: 1 },
      { id: "unit", label: "Volume unit", type: "select", def: "gallons", options: [{ value: "gallons", label: "Gallons" }, { value: "litres", label: "Litres" }] },
      { id: "flow", label: "Filter flow rate", type: "number", def: "300", unit: "GPH", min: 0, max: 5000, step: 10 },
      { id: "count", label: "Number of fish", type: "number", def: "10", min: 0, max: 500, step: 1 },
      { id: "length", label: "Average adult fish length", type: "number", def: "2", unit: "in", min: 0.1, max: 60, step: 0.1 },
    ],
    compute: (v) => {
      const rawVol = Math.max(0, n(v, "volume"));
      const unit = s(v, "unit", "gallons");
      const gallons = unit === "litres" ? rawVol * 0.264172 : rawVol;
      const flow = Math.max(0, n(v, "flow"));
      const count = Math.max(0, n(v, "count"));
      const length = Math.max(0, n(v, "length"));
      const bioload = count * Math.pow(length, 1.5);
      const stockingPct = gallons > 0 ? (bioload / gallons) * 100 : 0;
      let waterChangePct = Math.min(50, stockingPct * 0.25);
      const waterChangeGal = gallons * (waterChangePct / 100);
      const turnover = gallons > 0 ? flow / gallons : 0;
      let bioStatus = "Understocked";
      if (stockingPct >= 100) bioStatus = "Overstocked";
      else if (stockingPct >= 70) bioStatus = "Near capacity";
      else if (stockingPct >= 30) bioStatus = "Well stocked";
      return {
        cards: [
          { label: "Stocking capacity", value: `${fmt(stockingPct, 1)}%`, sub: bioStatus, accent: true },
          { label: "Weekly water change", value: `${fmt(waterChangeGal, 1)} gal`, sub: `${fmt(waterChangePct, 1)}% of tank` },
          { label: "Bioload status", value: bioStatus, sub: `Bioload score ${fmt(bioload, 1)}` },
          { label: "Filter turnover", value: `${fmt(turnover, 1)}x/hr`, sub: turnover >= 4 ? "Good turnover" : "Consider more flow" },
        ],
        table: {
          title: "Stocking breakdown",
          head: ["Metric", "Value"],
          rows: [
            ["Tank volume (gallons)", fmt(gallons, 1)],
            ["Fish count", fmt(count)],
            ["Average length (in)", fmt(length, 1)],
            ["Bioload score", fmt(bioload, 2)],
            ["Stocking %", `${fmt(stockingPct, 1)}%`],
            ["Recommended weekly change", `${fmt(waterChangeGal, 1)} gal (${fmt(waterChangePct, 1)}%)`],
            ["Filter turnover", `${fmt(turnover, 1)}x/hour`],
          ],
        },
        summary: `Your tank is at ${fmt(stockingPct, 1)}% stocking capacity (${bioStatus.toLowerCase()}); aim for roughly ${fmt(waterChangeGal, 1)} gallons weekly.`,
      };
    },
    intro:
      "Overstocking is the single biggest cause of ammonia spikes and dead fish in home aquariums. This calculator estimates bioload from fish count and average adult size, converts it into a stocking percentage, and recommends a weekly water change volume plus filter turnover check so your tank stays balanced.",
    sections: [
      { h: "Why length matters more than fish count", body: "A single 6-inch oscar produces vastly more waste than six neon tetras, which is why this tool weights bioload by length raised to the power of 1.5 rather than counting heads. Always plan stocking around the adult size a fish will reach, not the juvenile size sold in stores." },
      { h: "Reading the stocking percentage", body: "Under 30% leaves room to grow a community tank; 30-70% is a healthy, well-planted stocking level for most freshwater setups; above 100% means your filtration and water changes need to work overtime, and fish growth may stunt. Species with heavy bioloads like goldfish and cichlids often need lower percentages than the raw number suggests." },
      { h: "Setting your weekly water change", body: "The 25%-of-stocking rule keeps nitrate accumulation in check between full water tests. A lightly stocked 20-gallon tank might only need a 5-10% weekly change, while a heavily stocked tank should be changed closer to the 50% cap to prevent nitrate creep and pH drift." },
      { h: "Filter turnover as a sanity check", body: "Most freshwater tanks want 4-8x turnover per hour; heavily stocked or messy fish like goldfish benefit from 8-10x. If your turnover figure comes back under 4x, consider a stronger filter or a second unit rather than skipping water changes." },
    ],
    staticTable: {
      title: "Typical bioload reference by fish size",
      head: ["Fish length", "Bioload score (single fish)", "Suggested minimum tank"],
      rows: [
        ["1 in (tetra, shrimp)", "1.0", "5 gal per fish"],
        ["2 in (guppy, danio)", "2.8", "3 gal per fish"],
        ["3 in (platy, barb)", "5.2", "5 gal per fish"],
        ["4 in (angelfish juvenile)", "8.0", "15 gal per fish"],
        ["6 in (oscar juvenile)", "14.7", "40 gal per fish"],
        ["8 in (large cichlid)", "22.6", "75 gal per fish"],
        ["12 in (large goldfish)", "41.6", "40+ gal per fish"],
      ],
    },
    faqs: [
      { q: "How is aquarium bioload calculated here?", a: "Bioload is estimated as fish count multiplied by average adult length raised to the power 1.5, a simplified proxy used by hobbyists to approximate waste output better than a flat inch-per-gallon rule." },
      { q: "Is the classic 'one inch per gallon' rule accurate?", a: "It is a rough beginner guideline that breaks down for deep-bodied or messy fish. This calculator's length-based exponent gives a more realistic picture for stocky species like goldfish and cichlids." },
      { q: "What stocking percentage should I aim for?", a: "Most stable community tanks sit between 30% and 70%. Going above 100% usually means more frequent water changes, stronger filtration, or fewer fish." },
      { q: "How often should I actually test water instead of relying on this?", a: "Use this calculator to plan a baseline schedule, then confirm with an ammonia, nitrite and nitrate test kit weekly until you see stable readings, especially in newer tanks." },
      { q: "Does plant density change the water change recommendation?", a: "Heavily planted tanks absorb some nitrate naturally and can often reduce the suggested percentage by 5-10 points, but this tool assumes a moderately planted or bare setup for a conservative estimate." },
      { q: "Why does the water change percentage cap at 50%?", a: "Changing more than half the water at once risks shocking fish with sudden shifts in temperature, pH and mineral content, so 50% is treated as a practical ceiling even for very overstocked tanks." },
      { q: "How do I convert litres to gallons for this tool?", a: "Select litres from the unit dropdown and enter your tank volume directly; the calculator converts internally at 0.264172 gallons per litre before running the formulas." },
      { q: "What filter flow rate should I choose?", a: "Use the GPH (gallons per hour) rating printed on your filter box. If you run multiple filters, add their rated flows together before entering the total." },
      { q: "Does this calculator account for saltwater or reef bioload?", a: "No, saltwater and reef systems have different livestock-per-gallon and filtration needs; this tool is calibrated for freshwater community and semi-aggressive setups." },
      { q: "My turnover is fine but stocking is over 100% — what should I do?", a: "Prioritize rehoming or upgrading tank size over adding more filtration, since filtration reduces toxin levels but does not create the physical swimming space and territory fish need." },
    ],
    formula: {
      expressions: [
        "Bioload = Count × Length^1.5",
        "Stocking% = (Bioload / Gallons) × 100",
        "WaterChange% = min(50, Stocking% × 0.25)",
        "Turnover = FilterGPH / Gallons",
      ],
      variables: [
        { name: "Count", meaning: "Number of fish in the tank" },
        { name: "Length", meaning: "Average adult fish length in inches" },
        { name: "Gallons", meaning: "Tank volume converted to US gallons" },
      ],
    },
    related: ["hydroponic-ppm-calculator", "chicken-coop-space-calculator", "lawn-fertilizer-calculator", "solar-string-voltage-calculator"],
  },

  {
    slug: "hydroponic-ppm-calculator",
    cluster: "lifestyle",
    name: "Hydroponic PPM & EC",
    h1: "Hydroponic PPM & EC NPK Nutrient Solution Dilution Calculator",
    title: "Hydroponic PPM & EC Nutrient Calculator (2026)",
    description:
      "Calculate the exact nutrient dose to hit target PPM instantly. Free online hydroponic PPM and EC calculator with live formulas, unit conversion and a step-by-step breakdown.",
    keywords: [
      "hydroponic ppm calculator by crop",
      "ec to ppm conversion calculator",
      "nutrient solution dilution calculator",
      "how much nutrient to add hydroponics",
      "target ppm for lettuce tomato cannabis",
      "npk mixing ratio calculator",
    ],
    inputs: [
      { id: "volume", label: "Reservoir volume", type: "number", def: "20", unit: "L", min: 0.1, max: 10000, step: 1 },
      { id: "unit", label: "Volume unit", type: "select", def: "litres", options: [{ value: "litres", label: "Litres" }, { value: "gallons", label: "Gallons" }] },
      { id: "current", label: "Current water PPM", type: "number", def: "50", unit: "ppm", min: 0, max: 3000, step: 1 },
      { id: "crop", label: "Crop preset", type: "select", def: "lettuce", options: [
        { value: "lettuce", label: "Lettuce (700 ppm)" },
        { value: "tomato", label: "Tomato (1200 ppm)" },
        { value: "cannabis", label: "Cannabis (900 ppm)" },
        { value: "herbs", label: "Herbs (800 ppm)" },
        { value: "custom", label: "Custom target below" },
      ] },
      { id: "target", label: "Target PPM (used if custom)", type: "number", def: "900", unit: "ppm", min: 0, max: 3000, step: 10 },
      { id: "factor", label: "Stock concentration factor", type: "number", def: "1", unit: "g/L per 100ppm", min: 0.01, max: 10, step: 0.01 },
    ],
    compute: (v) => {
      const rawVol = Math.max(0.0001, n(v, "volume"));
      const unit = s(v, "unit", "litres");
      const litres = unit === "gallons" ? rawVol * 3.78541 : rawVol;
      const current = Math.max(0, n(v, "current"));
      const crop = s(v, "crop", "lettuce");
      const cropTargets: Record<string, number> = { lettuce: 700, tomato: 1200, cannabis: 900, herbs: 800 };
      const target = crop === "custom" ? Math.max(0, n(v, "target")) : (cropTargets[crop] ?? Math.max(0, n(v, "target")));
      const factor = Math.max(0.0001, n(v, "factor"));
      const delta = Math.max(0, target - current);
      const ec = target / 500;
      const grams = (delta / 100) * factor * litres;
      const finalPpm = current + delta;
      return {
        cards: [
          { label: "PPM to add (delta)", value: `${fmt(delta, 0)} ppm`, sub: `From ${fmt(current)} to ${fmt(target)}`, accent: true },
          { label: "Target EC", value: `${fmt(ec, 2)} mS/cm`, sub: "Using 500 scale" },
          { label: "Nutrient to add", value: `${fmt(grams, 1)} g`, sub: `≈ ${fmt(grams, 1)} ml if liquid` },
          { label: "Final solution PPM", value: `${fmt(finalPpm, 0)} ppm`, sub: `${fmt(litres, 1)} L reservoir` },
        ],
        table: {
          title: "Dilution breakdown",
          head: ["Metric", "Value"],
          rows: [
            ["Reservoir volume (L)", fmt(litres, 1)],
            ["Current PPM", fmt(current, 0)],
            ["Target PPM", fmt(target, 0)],
            ["Delta PPM", fmt(delta, 0)],
            ["Target EC (500 scale)", fmt(ec, 2)],
            ["Nutrient to add", `${fmt(grams, 1)} g / ml`],
            ["Final PPM", fmt(finalPpm, 0)],
          ],
        },
        summary: `Add about ${fmt(grams, 1)} g of concentrated nutrient to your ${fmt(litres, 1)} L reservoir to raise PPM from ${fmt(current)} to ${fmt(target)}.`,
      };
    },
    intro:
      "Getting nutrient strength right is the difference between vigorous hydroponic growth and nutrient burn or deficiency. This calculator takes your reservoir's current PPM, a crop-specific or custom target, and your stock solution's strength to tell you exactly how much concentrate to dose, plus the equivalent EC reading.",
    sections: [
      { h: "PPM, EC and TDS explained", body: "PPM (parts per million) and EC (electrical conductivity) measure the same thing from different angles: EC measures how well the solution conducts electricity, and most consumer meters convert that to a PPM estimate using either a 500 or 700 scale. This tool uses the common 500 scale, so EC = PPM / 500." },
      { h: "Why crop targets differ so much", body: "Leafy greens like lettuce are light feeders and stay healthiest around 560-840 ppm, while fruiting crops like tomatoes need heavier feeding, often 1050-1400 ppm during fruiting. Cannabis and herbs sit in between and shift across vegetative and flowering stages, so treat the presets as a starting midpoint, not a fixed target." },
      { h: "Reading your stock concentration factor", body: "Nutrient labels vary widely; the 'g per litre per 100 ppm' factor tells the calculator how strong your particular concentrate is. If your label states a dose like '2 ml per litre raises EC by 0.4', convert that to a per-100-ppm factor (0.4 EC ≈ 200 ppm, so 2 ml per 200 ppm = 1 ml per 100 ppm) and enter it here." },
      { h: "Practical dosing tips", body: "Always add nutrients gradually and re-measure with a calibrated PPM/EC meter rather than trusting a single calculation, since water source PPM and nutrient product strength both vary. Recheck after 15-30 minutes of circulation, as solids can take time to fully dissolve in cooler reservoirs." },
    ],
    staticTable: {
      title: "Typical target PPM ranges by crop stage",
      head: ["Crop", "Seedling", "Vegetative", "Flowering/Fruiting"],
      rows: [
        ["Lettuce & leafy greens", "400-500", "560-840", "n/a"],
        ["Tomato", "500-800", "1000-1200", "1200-1400"],
        ["Cannabis", "300-400", "700-900", "900-1100"],
        ["Herbs (basil, mint)", "400-600", "700-900", "n/a"],
        ["Strawberry", "560-700", "800-1000", "1000-1200"],
        ["Peppers", "600-800", "1000-1200", "1200-1450"],
      ],
    },
    faqs: [
      { q: "What's the difference between the 500 and 700 PPM scales?", a: "Both convert EC to PPM but with different multipliers; a 700-scale meter will read about 40% higher than a 500-scale meter for the same EC. This calculator assumes the 500 scale, common in North American hydroponic meters." },
      { q: "How do I find my stock solution's concentration factor?", a: "Check the manufacturer's dosing chart for how many ml or grams per litre raises EC or PPM by a known amount, then rescale that ratio to grams per litre per 100 ppm as described in the sections above." },
      { q: "Can I use this for soil fertigation instead of hydroponics?", a: "The dilution math works the same way, but soil buffers nutrient availability differently than an inert hydroponic medium, so soil growers should generally target the lower end of any crop's PPM range." },
      { q: "Why is my final PPM different from what I measured?", a: "Tap or well water often carries dissolved minerals beyond what a cheap meter reports, and different nutrient salts affect conductivity unevenly, so lab-calibrated dosing charts remain more precise than any single-formula estimate." },
      { q: "Should I zero out my meter before adding nutrients?", a: "Always measure your current water PPM first and enter it as the current PPM input, since this tool calculates the delta needed rather than assuming your source water is at zero." },
      { q: "How often should I top up nutrients in a recirculating system?", a: "Check PPM daily in active recirculating systems, since plants uptake water faster than nutrients, causing PPM to drift upward over the week even without adding more concentrate." },
      { q: "What happens if I add too much nutrient at once?", a: "Overshooting PPM risks root burn and nutrient lockout; dilute the reservoir with plain water rather than trying to precisely reverse a dosing error." },
      { q: "Does temperature affect PPM readings?", a: "Yes, most meters apply automatic temperature compensation, but readings taken far from 25°C/77°C reference temperature can drift; let solution reach room temperature before dosing decisions." },
      { q: "Can I use this calculator for multi-part nutrient systems (A+B)?", a: "Yes, run the calculation once per part using each part's individual concentration factor, then add both amounts to the reservoir, keeping parts separate until fully diluted in water." },
      { q: "Is a higher PPM always better for faster growth?", a: "No, exceeding a crop's tolerance range causes nutrient burn and reduced uptake; matching the crop-stage target closely produces healthier growth than maximizing PPM." },
    ],
    formula: {
      expressions: [
        "DeltaPPM = TargetPPM − CurrentPPM",
        "EC = TargetPPM / 500",
        "Grams = (DeltaPPM / 100) × Factor × Litres",
        "FinalPPM = CurrentPPM + DeltaPPM",
      ],
      variables: [
        { name: "Factor", meaning: "Grams (or ml) of concentrate per litre needed to raise PPM by 100" },
        { name: "Litres", meaning: "Reservoir volume converted to litres" },
      ],
    },
    related: ["aquarium-stocking-calculator", "lawn-fertilizer-calculator", "concrete-bag-calculator", "solar-string-voltage-calculator"],
  },

  {
    slug: "solar-string-voltage-calculator",
    cluster: "lifestyle",
    name: "Solar String Voltage",
    h1: "Solar Panel String Voltage & Inverter MPPT Matching Calculator",
    title: "Solar String Voltage & MPPT Matching Calculator (2026)",
    description:
      "Calculate your solar string's cold and hot voltage instantly. Free online solar string voltage calculator with live formulas, unit conversion and a step-by-step breakdown.",
    keywords: [
      "solar panel string voltage calculator",
      "voc temperature correction calculator",
      "how many solar panels in series for inverter",
      "mppt voltage range matching calculator",
      "max panels per string calculator",
      "cold weather voc calculation solar",
    ],
    inputs: [
      { id: "voc", label: "Panel Voc (open circuit voltage)", type: "number", def: "49.5", unit: "V", min: 1, max: 200, step: 0.1 },
      { id: "vmp", label: "Panel Vmp (voltage at max power)", type: "number", def: "41.2", unit: "V", min: 1, max: 200, step: 0.1 },
      { id: "coeff", label: "Temp coefficient of Voc", type: "number", def: "-0.29", unit: "%/°C", min: -1, max: 0, step: 0.01 },
      { id: "minTemp", label: "Lowest ambient temperature", type: "number", def: "-10", unit: "°C", min: -50, max: 30, step: 1 },
      { id: "maxTemp", label: "Highest cell temperature", type: "number", def: "65", unit: "°C", min: 25, max: 100, step: 1 },
      { id: "panels", label: "Panels per string", type: "number", def: "10", min: 1, max: 40, step: 1 },
      { id: "invMaxV", label: "Inverter max DC voltage", type: "number", def: "600", unit: "V", min: 50, max: 1500, step: 10 },
      { id: "mpptMin", label: "Inverter MPPT min voltage", type: "number", def: "150", unit: "V", min: 10, max: 1000, step: 10 },
    ],
    compute: (v) => {
      const voc = Math.max(0, n(v, "voc"));
      const vmp = Math.max(0, n(v, "vmp"));
      const coeff = n(v, "coeff", -0.29);
      const minT = n(v, "minTemp");
      const maxT = n(v, "maxTemp");
      const panels = Math.max(1, Math.round(n(v, "panels", 1)));
      const invMaxV = Math.max(1, n(v, "invMaxV"));
      const mpptMin = Math.max(0, n(v, "mpptMin"));

      const vocColdSingle = voc * (1 + (coeff / 100) * (minT - 25));
      const vmpHotSingle = vmp * (1 + (coeff / 100) * (maxT - 25));

      const vocColdString = vocColdSingle * panels;
      const vmpHotString = vmpHotSingle * panels;

      const pass = vocColdString <= invMaxV && vmpHotString >= mpptMin;

      const maxPanelsAllowed = vocColdSingle > 0 ? Math.floor(invMaxV / vocColdSingle) : 0;

      return {
        cards: [
          { label: "Cold Voc string voltage", value: `${fmt(vocColdString, 1)} V`, sub: `${panels} panels in series`, accent: true },
          { label: "Hot Vmp string voltage", value: `${fmt(vmpHotString, 1)} V`, sub: "At high cell temperature" },
          { label: "Status", value: pass ? "Pass" : "Fail", sub: pass ? "Within inverter limits" : "Adjust string length" },
          { label: "Max panels allowed", value: fmt(maxPanelsAllowed), sub: `Given inverter max ${fmt(invMaxV)} V` },
        ],
        table: {
          title: "String voltage breakdown",
          head: ["Metric", "Value"],
          rows: [
            ["Panel Voc", `${fmt(voc, 1)} V`],
            ["Panel Vmp", `${fmt(vmp, 1)} V`],
            ["Cold Voc (single panel)", `${fmt(vocColdSingle, 2)} V`],
            ["Hot Vmp (single panel)", `${fmt(vmpHotSingle, 2)} V`],
            ["Cold Voc string total", `${fmt(vocColdString, 1)} V`],
            ["Hot Vmp string total", `${fmt(vmpHotString, 1)} V`],
            ["Inverter max DC voltage", `${fmt(invMaxV)} V`],
            ["Inverter MPPT min voltage", `${fmt(mpptMin)} V`],
          ],
        },
        summary: pass
          ? `${panels} panels in series stay safely under ${fmt(invMaxV)} V cold and above ${fmt(mpptMin)} V hot.`
          : `${panels} panels in series may exceed inverter limits — consider up to ${fmt(maxPanelsAllowed)} panels per string.`,
      };
    },
    intro:
      "Sizing a solar string wrong can trip an inverter's overvoltage protection on a freezing morning or drop below MPPT range on a scorching afternoon. This calculator applies the panel's temperature coefficient to both extremes, sums the string voltage, and checks it against your inverter's DC voltage window.",
    sections: [
      { h: "Why cold weather raises voltage", body: "Solar cells behave opposite to what most people expect: voltage rises as temperature drops. A panel rated 49.5V Voc at 25°C standard test conditions can climb well past 55V on a -10°C morning, and that increase multiplies across every panel in the string, which is why inverter overvoltage trips most often happen on clear, cold, sunny mornings." },
      { h: "The temperature coefficient formula", body: "Manufacturers publish a Voc temperature coefficient, typically between -0.25% and -0.45% per °C for crystalline silicon panels. The corrected voltage is Voc × (1 + (coefficient/100) × (temperature − 25)), since datasheet ratings are always measured at 25°C standard test conditions." },
      { h: "Balancing cold-limit and hot-limit sizing", body: "Design the string two ways: check the coldest expected ambient temperature against the inverter's maximum input voltage using Voc, and check the hottest expected cell temperature (often 20-25°C above ambient air temperature) against the inverter's minimum MPPT voltage using Vmp. A string can fail on either end." },
      { h: "What to do if your string fails", body: "If cold Voc exceeds the inverter's max voltage, remove one or more panels from the string or split into two shorter strings. If hot Vmp drops below the MPPT minimum, add panels to the string or choose an inverter with a lower MPPT floor." },
    ],
    staticTable: {
      title: "Typical Voc correction by climate",
      head: ["Coldest design temp", "Coefficient", "Voltage rise vs STC", "Recommended safety margin"],
      rows: [
        ["-5°C", "-0.29%/°C", "+8.7%", "10%"],
        ["-10°C", "-0.29%/°C", "+10.2%", "10-12%"],
        ["-20°C", "-0.29%/°C", "+13.1%", "12-15%"],
        ["-30°C", "-0.29%/°C", "+15.9%", "15%"],
        ["-40°C", "-0.35%/°C", "+22.8%", "18-20%"],
      ],
    },
    faqs: [
      { q: "What temperature should I use for the coldest ambient input?", a: "Use your site's record low temperature (often the ASHRAE 99.6% or 2% extreme minimum design temperature for your region), not just an average winter low, since inverter overvoltage trips on the coldest clear mornings." },
      { q: "Where do I find my panel's temperature coefficient?", a: "It's printed on the panel datasheet as 'Temperature coefficient of Voc,' usually expressed as a negative percentage per degree Celsius, typically between -0.25% and -0.45% for standard crystalline panels." },
      { q: "Why use cell temperature instead of ambient for the hot check?", a: "Panel surfaces run significantly hotter than the surrounding air due to solar absorption, often 20-30°C above ambient on a still, sunny day, so cell temperature gives a realistic worst-case low-voltage scenario." },
      { q: "What happens if my string voltage exceeds the inverter's max input?", a: "The inverter's overvoltage protection will shut the string down entirely to protect its components, which can also void the inverter warranty if it happens repeatedly, so always keep a safety margin below the rated maximum." },
      { q: "Is a bigger safety margin always better?", a: "Not necessarily — leaving excessive margin means running the inverter below its optimal DC input range for longer parts of the day, slightly reducing energy harvest, so match the margin to your actual climate extremes." },
      { q: "Can I mix panel models in one string?", a: "It's not recommended; different Voc, Vmp and temperature coefficients mean the string operates at the lowest common performance point and complicates accurate voltage calculations like the ones here." },
      { q: "Does altitude affect these calculations?", a: "High-altitude installations sometimes require additional voltage derating per local electrical code due to reduced dielectric strength of air, which is separate from the temperature correction shown here." },
      { q: "How many panels can I safely put in one string?", a: "The 'max panels allowed' figure divides your inverter's max DC voltage by the single-panel cold Voc and rounds down, giving the largest series string that stays under the limit at your coldest design temperature." },
      { q: "What if my hot Vmp string voltage falls below MPPT minimum?", a: "The inverter may fail to track maximum power point or shut down entirely during hot afternoons; adding panels to the string or selecting a lower-MPPT-floor inverter resolves this." },
      { q: "Does shading affect the voltage calculations here?", a: "No, this tool assumes unshaded, matched-string conditions; partial shading changes current more than voltage in most string topologies but can still cause power loss not captured by this calculator." },
    ],
    formula: {
      expressions: [
        "Voc_Cold(single) = Voc × (1 + (Coeff/100) × (MinTemp − 25))",
        "Vmp_Hot(single) = Vmp × (1 + (Coeff/100) × (MaxTemp − 25))",
        "StringVoc_Cold = Voc_Cold(single) × Panels",
        "StringVmp_Hot = Vmp_Hot(single) × Panels",
      ],
      variables: [
        { name: "Coeff", meaning: "Manufacturer temperature coefficient of Voc, %/°C (negative)" },
        { name: "MinTemp", meaning: "Lowest expected ambient temperature, °C" },
        { name: "MaxTemp", meaning: "Highest expected cell temperature, °C" },
      ],
    },
    related: ["kva-to-watts-calculator", "nas-power-cost-calculator", "concrete-bag-calculator", "aquarium-stocking-calculator"],
  },

  {
    slug: "concrete-bag-calculator",
    cluster: "lifestyle",
    name: "Concrete Bags",
    h1: "Concrete Bag Calculator: How Many 80lb, 60lb & 50lb Bags for Slab",
    title: "Concrete Bag Calculator: 80lb, 60lb, 50lb Bags (2026)",
    description:
      "Calculate exactly how many bags of concrete you need instantly. Free online concrete bag calculator with live formulas, unit conversion and a step-by-step breakdown.",
    keywords: [
      "concrete bag calculator 80lb 60lb 50lb",
      "how many bags of concrete for a slab",
      "cubic yards of concrete calculator",
      "concrete calculator round vs rectangular",
      "80 lb bag concrete coverage calculator",
      "concrete cost estimator by bag",
    ],
    inputs: [
      { id: "shape", label: "Slab shape", type: "select", def: "rect", options: [{ value: "rect", label: "Rectangular" }, { value: "round", label: "Round" }] },
      { id: "length", label: "Length", type: "number", def: "10", unit: "ft", min: 0.1, max: 1000, step: 0.1 },
      { id: "width", label: "Width / diameter", type: "number", def: "10", unit: "ft", min: 0.1, max: 1000, step: 0.1 },
      { id: "depth", label: "Depth", type: "number", def: "4", unit: "in", min: 0.5, max: 48, step: 0.5 },
      { id: "bagSize", label: "Bag size", type: "select", def: "80", options: [
        { value: "80", label: "80 lb (0.60 cu ft)" },
        { value: "60", label: "60 lb (0.45 cu ft)" },
        { value: "50", label: "50 lb (0.375 cu ft)" },
      ] },
      { id: "waste", label: "Waste allowance", type: "number", def: "10", unit: "%", min: 0, max: 50, step: 1 },
      { id: "bagPrice", label: "Price per bag (optional)", type: "number", def: "6.5", unit: "$", min: 0, max: 100, step: 0.1 },
    ],
    compute: (v) => {
      const shape = s(v, "shape", "rect");
      const length = Math.max(0, n(v, "length"));
      const width = Math.max(0, n(v, "width"));
      const depthFt = Math.max(0, n(v, "depth")) / 12;
      const waste = Math.max(0, n(v, "waste"));
      const bagPrice = Math.max(0, n(v, "bagPrice"));
      const bagSize = s(v, "bagSize", "80");
      const cuFtPerBag: Record<string, number> = { "80": 0.6, "60": 0.45, "50": 0.375 };
      const perBag = cuFtPerBag[bagSize] ?? 0.6;

      let areaSqFt = 0;
      if (shape === "round") {
        const radius = width / 2;
        areaSqFt = Math.PI * radius * radius;
      } else {
        areaSqFt = length * width;
      }
      const cuFtBase = areaSqFt * depthFt;
      const cuFt = cuFtBase * (1 + waste / 100);
      const cuYd = cuFt / 27;
      const bagsRequired = perBag > 0 ? Math.ceil(cuFt / perBag) : 0;
      const cost = bagsRequired * bagPrice;

      return {
        cards: [
          { label: "Bags required", value: fmt(bagsRequired), sub: `${bagSize} lb bags`, accent: true },
          { label: "Cubic feet", value: fmt(cuFt, 2), sub: `Includes ${fmt(waste)}% waste` },
          { label: "Cubic yards", value: fmt(cuYd, 3), sub: "For ready-mix comparison" },
          { label: "Estimated cost", value: money(cost), sub: `At ${money(bagPrice)}/bag` },
        ],
        table: {
          title: "Concrete volume breakdown",
          head: ["Metric", "Value"],
          rows: [
            ["Shape", shape === "round" ? "Round" : "Rectangular"],
            ["Area (sq ft)", fmt(areaSqFt, 2)],
            ["Depth (ft)", fmt(depthFt, 3)],
            ["Base volume (cu ft)", fmt(cuFtBase, 2)],
            ["With waste allowance (cu ft)", fmt(cuFt, 2)],
            ["Cubic yards", fmt(cuYd, 3)],
            ["Bags required", fmt(bagsRequired)],
            ["Estimated cost", money(cost)],
          ],
        },
        summary: `You'll need ${fmt(bagsRequired)} bags of ${bagSize} lb concrete for a ${fmt(cuFt, 2)} cu ft pour, about ${money(cost)}.`,
      };
    },
    intro:
      "Buying too few bags means a mid-pour trip to the hardware store; buying too many wastes money and leftover mix that hardens in the bag. This calculator converts your slab dimensions into cubic feet and cubic yards, then rounds up to the nearest whole bag for 80lb, 60lb or 50lb mixes, with an optional cost estimate.",
    sections: [
      { h: "Rectangular vs round slabs", body: "For rectangular slabs, volume is simply length × width × depth. For round slabs like a pier footing or fire pit base, the tool uses π × radius² × depth, where radius is half the diameter you enter. Always measure diameter, not radius, in the width field for round shapes." },
      { h: "Why bag yield varies by weight", body: "An 80lb bag yields about 0.60 cubic feet of mixed concrete, a 60lb bag about 0.45 cubic feet, and a 50lb bag about 0.375 cubic feet — the yield per pound is nearly constant across sizes, so smaller bags simply mean more bags to carry and mix, not better value." },
      { h: "Why waste allowance matters", body: "Uneven subgrade, spillage during mixing, and over-excavation typically consume 5-10% more material than the theoretical volume. Slabs with irregular edges or hand-dug footings should use 10-15% waste allowance; simple rectangular forms can often use 5%." },
      { h: "Bags vs ready-mix trucks", body: "Once a pour exceeds roughly 1-1.5 cubic yards (about 45-60 bags of 80lb mix), a ready-mix delivery usually becomes more cost-effective and far less physical labor than hand-mixing dozens of bags, so use the cubic yard figure to decide which route makes sense." },
    ],
    staticTable: {
      title: "Bags needed per cubic yard by bag size",
      head: ["Bag size", "Cu ft per bag", "Bags per cubic yard"],
      rows: [
        ["80 lb", "0.60", "45"],
        ["60 lb", "0.45", "60"],
        ["50 lb", "0.375", "72"],
        ["40 lb", "0.30", "90"],
      ],
    },
    faqs: [
      { q: "How many 80 lb bags of concrete do I need for a 10x10 slab at 4 inches?", a: "A 10x10 ft slab at 4 inches deep is about 33.3 cubic feet before waste; with 10% waste that's roughly 36.7 cubic feet, requiring about 62 bags of 80 lb concrete." },
      { q: "What depth should I use for a standard patio slab?", a: "Most residential patio and walkway slabs are poured 4 inches deep; driveways for cars typically need 5-6 inches, and heavy vehicle areas may need 6-8 inches with reinforcement." },
      { q: "Is it cheaper to use 50 lb bags instead of 80 lb bags?", a: "Price per cubic foot is usually similar across bag sizes from the same brand, but 50 lb bags are easier to lift and mix by hand, making them a better choice for solo DIY pours despite needing more bags total." },
      { q: "How much waste allowance should I add for a round fire pit base?", a: "Round and irregular shapes typically warrant 10-15% waste allowance due to trimming and form imprecision, compared to 5-10% for a simple rectangular form." },
      { q: "At what pour size should I switch to ready-mix delivery?", a: "Once your calculated volume exceeds about 1 to 1.5 cubic yards, a ready-mix truck often costs less per cubic yard and saves significant labor compared to mixing 45+ bags by hand." },
      { q: "Does this calculator account for rebar or wire mesh volume?", a: "No, reinforcement takes up negligible volume and is not subtracted; however, it's essential for structural strength on slabs used for parking or heavy loads regardless of the concrete volume calculated." },
      { q: "How do I measure depth accurately for an uneven surface?", a: "Take multiple depth measurements across the form and average them, or measure at the deepest point and add extra waste allowance to account for the additional material needed in low spots." },
      { q: "Can I use this calculator for concrete footings and post holes?", a: "Yes for footings with a defined length, width and depth; for round post holes use the round shape option with diameter and depth, though very deep narrow holes may need extra waste allowance for spillage." },
      { q: "Why is my actual bag count higher than the calculator predicted?", a: "Uneven subgrade compaction, over-digging, and spillage during mixing commonly account for the gap; increasing the waste allowance percentage next time will better match real-world usage." },
      { q: "How much does a typical 80 lb bag of concrete cost?", a: "Prices vary by region and brand but commonly range from $5 to $8 per 80 lb bag at hardware stores; enter your local price in the cost field for an accurate project estimate." },
    ],
    formula: {
      expressions: [
        "Area = Length × Width (rectangular) or π × (Diameter/2)² (round)",
        "CubicFeet = Area × (Depth_in / 12) × (1 + Waste%/100)",
        "CubicYards = CubicFeet / 27",
        "Bags = ceil(CubicFeet / CuFtPerBag)",
      ],
      variables: [
        { name: "CuFtPerBag", meaning: "Yield per bag: 0.60 (80lb), 0.45 (60lb), 0.375 (50lb)" },
        { name: "Waste%", meaning: "Extra percentage added for spillage and subgrade unevenness" },
      ],
    },
    related: ["lawn-fertilizer-calculator", "chicken-coop-space-calculator", "solar-string-voltage-calculator", "hydroponic-ppm-calculator"],
  },

  {
    slug: "lawn-fertilizer-calculator",
    cluster: "lifestyle",
    name: "Lawn Fertilizer N-P-K",
    h1: "Lawn Fertilizer N-P-K Calculator: Exact Bag Weight per 1000 Sq Ft",
    title: "Lawn Fertilizer N-P-K Calculator (2026)",
    description:
      "Calculate exactly how much fertilizer to spread instantly. Free online lawn fertilizer calculator with live formulas, unit conversion and a step-by-step breakdown.",
    keywords: [
      "lawn fertilizer calculator by npk",
      "how much fertilizer per 1000 sq ft",
      "nitrogen rate calculator lawn",
      "fertilizer bags needed calculator",
      "npk application rate calculator",
      "lawn fertilizer coverage calculator",
    ],
    inputs: [
      { id: "area", label: "Lawn area", type: "number", def: "5000", unit: "sq ft", min: 100, max: 1000000, step: 100 },
      { id: "n", label: "Nitrogen (N) %", type: "number", def: "20", unit: "%", min: 0.1, max: 100, step: 0.1 },
      { id: "p", label: "Phosphorus (P) %", type: "number", def: "5", unit: "%", min: 0, max: 100, step: 0.1 },
      { id: "k", label: "Potassium (K) %", type: "number", def: "10", unit: "%", min: 0, max: 100, step: 0.1 },
      { id: "rate", label: "Target nitrogen rate", type: "number", def: "1", unit: "lb N/1000 sq ft", min: 0.1, max: 5, step: 0.1 },
      { id: "bagWeight", label: "Bag weight", type: "number", def: "40", unit: "lb", min: 1, max: 200, step: 1 },
    ],
    compute: (v) => {
      const area = Math.max(0, n(v, "area"));
      const nPct = Math.max(0.0001, n(v, "n")) / 100;
      const pPct = Math.max(0, n(v, "p")) / 100;
      const kPct = Math.max(0, n(v, "k")) / 100;
      const rate = Math.max(0, n(v, "rate"));
      const bagWeight = Math.max(0.0001, n(v, "bagWeight"));

      const productPer1000 = rate / nPct;
      const units1000 = area / 1000;
      const totalProduct = productPer1000 * units1000;
      const bagsRequired = Math.ceil(totalProduct / bagWeight);
      const actualP = productPer1000 * pPct * units1000;
      const actualK = productPer1000 * kPct * units1000;

      return {
        cards: [
          { label: "Product per 1000 sq ft", value: `${fmt(productPer1000, 2)} lb`, sub: `To deliver ${fmt(rate, 2)} lb N`, accent: true },
          { label: "Total product needed", value: `${fmt(totalProduct, 1)} lb`, sub: `Over ${fmt(area)} sq ft` },
          { label: "Bags required", value: fmt(bagsRequired), sub: `${fmt(bagWeight)} lb bags` },
          { label: "Actual P / K applied", value: `${fmt(actualP, 1)} / ${fmt(actualK, 1)} lb`, sub: "Total phosphorus / potassium" },
        ],
        table: {
          title: "Fertilizer application breakdown",
          head: ["Metric", "Value"],
          rows: [
            ["Lawn area", `${fmt(area)} sq ft`],
            ["N-P-K analysis", `${fmt(n(v, "n"), 1)}-${fmt(n(v, "p"), 1)}-${fmt(n(v, "k"), 1)}`],
            ["Target N rate", `${fmt(rate, 2)} lb/1000 sq ft`],
            ["Product per 1000 sq ft", `${fmt(productPer1000, 2)} lb`],
            ["Total product needed", `${fmt(totalProduct, 1)} lb`],
            ["Bags required", fmt(bagsRequired)],
            ["Actual phosphorus applied", `${fmt(actualP, 1)} lb`],
            ["Actual potassium applied", `${fmt(actualK, 1)} lb`],
          ],
        },
        summary: `Apply ${fmt(totalProduct, 1)} lb of your ${fmt(n(v, "n"), 0)}-${fmt(n(v, "p"), 0)}-${fmt(n(v, "k"), 0)} fertilizer across ${fmt(area)} sq ft, about ${bagsRequired} bag(s).`,
      };
    },
    intro:
      "Fertilizer bags list an N-P-K percentage, but turning that into 'how many pounds should I spread' requires a quick conversion most homeowners skip. This calculator uses your lawn area, the bag's nitrogen percentage, and your target nitrogen rate to compute exactly how much product to buy and spread, plus the resulting phosphorus and potassium totals.",
    sections: [
      { h: "Understanding the N-P-K numbers", body: "The three numbers on a fertilizer bag, like 20-5-10, represent the percentage by weight of nitrogen, available phosphate and soluble potash respectively. A 40 lb bag of 20-5-10 contains 8 lb of actual nitrogen (20% of 40), 2 lb of phosphate, and 4 lb of potash." },
      { h: "Why nitrogen rate drives the calculation", body: "Lawn care recommendations are almost universally expressed as pounds of actual nitrogen per 1000 square feet, typically 0.5-1 lb per application and 2-4 lb total per year for cool-season grasses. This calculator solves for product weight by dividing the target nitrogen rate by the fertilizer's nitrogen percentage." },
      { h: "Reading your phosphorus and potassium output", body: "Because phosphorus and potassium percentages are usually lower than nitrogen, the same product weight that delivers your target nitrogen rate often delivers a smaller absolute amount of P and K. Check these against soil test recommendations, since many established lawns need little to no additional phosphorus." },
      { h: "Timing and splitting applications", body: "Rather than applying the full seasonal nitrogen total at once, split it across 3-4 applications through the growing season to avoid burning the lawn and to match grass growth cycles. Recalculate this tool for each application using that visit's target rate rather than the annual total." },
    ],
    staticTable: {
      title: "Common fertilizer product needed per 1000 sq ft (1 lb N target)",
      head: ["N-P-K analysis", "Product per 1000 sq ft", "N per 40 lb bag"],
      rows: [
        ["10-10-10", "10.0 lb", "4.0 lb"],
        ["15-5-10", "6.7 lb", "6.0 lb"],
        ["20-5-10", "5.0 lb", "8.0 lb"],
        ["29-0-4", "3.4 lb", "11.6 lb"],
        ["32-0-10", "3.1 lb", "12.8 lb"],
        ["46-0-0 (urea)", "2.2 lb", "18.4 lb"],
      ],
    },
    faqs: [
      { q: "How much 20-5-10 fertilizer do I need for a 5000 sq ft lawn at 1 lb N?", a: "You'd need 5 lb of product per 1000 sq ft, so 25 lb total across 5000 sq ft, which fits within a single standard 40 lb bag." },
      { q: "How many pounds of nitrogen should my lawn get per year?", a: "Most cool-season lawns need 2-4 lb of actual nitrogen per 1000 sq ft per year split across multiple feedings, while warm-season lawns often need 3-6 lb depending on grass type and climate." },
      { q: "What does the second number (phosphorus) on a fertilizer bag mean?", a: "It represents the percentage of available phosphate (P2O5) by weight, which supports root development; many established lawns need very little added phosphorus unless a soil test shows deficiency." },
      { q: "Can I use this calculator for granular and liquid fertilizers?", a: "Yes for granular products directly; for liquid concentrates, convert the product's nitrogen percentage and density to a weight-per-volume basis first, since this tool outputs results in pounds." },
      { q: "Why does a lower nitrogen percentage require more product?", a: "The nitrogen rate you want stays fixed, so a fertilizer with a lower nitrogen percentage must be applied in a larger total quantity to deliver the same amount of actual nitrogen per square foot." },
      { q: "Is it bad to apply more nitrogen than recommended?", a: "Yes, excess nitrogen can burn grass, increase disease susceptibility, and contribute to nutrient runoff into waterways; always stay within your target rate and local extension office guidelines." },
      { q: "How do I measure my lawn area accurately?", a: "Break irregular lawns into rectangles and triangles, measure each with a tape measure or a mapping app, then sum the areas; most home lawns are measured in the thousands of square feet." },
      { q: "Should I fertilize based on soil test results instead of a general rate?", a: "A soil test is always more precise, especially for phosphorus and potassium, since many lawns already have sufficient levels; use this calculator's nitrogen-driven output alongside soil test guidance for P and K." },
      { q: "What's a good target nitrogen rate for a spring feeding?", a: "Many university extension guides recommend 0.5-1 lb of nitrogen per 1000 sq ft for a typical spring or fall feeding, with slow-release products allowing rates toward the higher end safely." },
      { q: "How many bags should I buy if the total falls between bag sizes?", a: "The calculator rounds up to the next whole bag automatically, since spreaders can't measure fractional bags accurately and having leftover product for the next application is standard practice." },
    ],
    formula: {
      expressions: [
        "ProductPer1000 = TargetNRate / N%",
        "TotalProduct = ProductPer1000 × (Area / 1000)",
        "Bags = ceil(TotalProduct / BagWeight)",
        "ActualP = ProductPer1000 × P% × (Area/1000); ActualK = ProductPer1000 × K% × (Area/1000)",
      ],
      variables: [
        { name: "N%", meaning: "Nitrogen percentage listed on the fertilizer bag" },
        { name: "TargetNRate", meaning: "Desired pounds of actual nitrogen per 1000 sq ft" },
      ],
    },
    related: ["hydroponic-ppm-calculator", "concrete-bag-calculator", "chicken-coop-space-calculator", "aquarium-stocking-calculator"],
  },

  {
    slug: "bbq-charcoal-wood-calculator",
    cluster: "lifestyle",
    name: "BBQ Charcoal & Wood",
    h1: "BBQ Smoker Charcoal & Wood Chunk Consumption Calculator",
    title: "BBQ Smoker Charcoal & Wood Calculator (2026)",
    description:
      "Calculate exactly how much charcoal and wood you'll need instantly. Free online BBQ smoker fuel calculator with live formulas, unit conversion and a step-by-step breakdown.",
    keywords: [
      "how much charcoal for smoking brisket calculator",
      "smoker fuel consumption calculator",
      "wood chunks needed for smoking calculator",
      "charcoal per hour offset smoker",
      "how long to smoke a pork butt calculator",
      "bbq fuel cost estimator",
    ],
    inputs: [
      { id: "smoker", label: "Smoker type", type: "select", def: "offset", options: [
        { value: "offset", label: "Offset smoker (2.0 lb/hr factor)" },
        { value: "kettle", label: "Kettle grill (1.0 lb/hr factor)" },
        { value: "kamado", label: "Kamado (0.5 lb/hr factor)" },
      ] },
      { id: "cut", label: "Meat cut", type: "select", def: "brisket", options: [
        { value: "brisket", label: "Brisket (1.5 hr/lb)" },
        { value: "porkbutt", label: "Pork butt (1.5 hr/lb)" },
        { value: "ribs", label: "Ribs (fixed 5 hr)" },
        { value: "chicken", label: "Chicken (0.75 hr/lb)" },
      ] },
      { id: "weight", label: "Meat weight", type: "number", def: "12", unit: "lb", min: 0.5, max: 100, step: 0.5 },
      { id: "targetTemp", label: "Target smoker temp", type: "number", def: "250", unit: "°F", min: 200, max: 300, step: 5 },
      { id: "ambientTemp", label: "Ambient temperature", type: "number", def: "70", unit: "°F", min: -20, max: 110, step: 1 },
      { id: "charcoalPrice", label: "Charcoal price (optional)", type: "number", def: "1.2", unit: "$/lb", min: 0, max: 20, step: 0.1 },
    ],
    compute: (v) => {
      const smoker = s(v, "smoker", "offset");
      const smokerFactors: Record<string, number> = { offset: 2.0, kettle: 1.0, kamado: 0.5 };
      const factor = smokerFactors[smoker] ?? 1.0;
      const cut = s(v, "cut", "brisket");
      const weight = Math.max(0, n(v, "weight"));
      const targetTemp = n(v, "targetTemp", 250);
      const ambientTemp = n(v, "ambientTemp", 70);
      const price = Math.max(0, n(v, "charcoalPrice"));

      let cookHours = 0;
      if (cut === "ribs") cookHours = 5;
      else if (cut === "chicken") cookHours = 0.75 * weight;
      else cookHours = 1.5 * weight;

      const tempAdjust = 1 + Math.max(0, (250 - targetTemp)) * 0.004;
      cookHours = cookHours * tempAdjust;

      const coldFactor = ambientTemp < 50 ? 1 + (50 - ambientTemp) * 0.01 : 1;

      const charcoalLbs = cookHours * factor * coldFactor;
      const woodChunks = Math.ceil(cookHours * 1.5);
      const fuelCost = charcoalLbs * price;

      return {
        cards: [
          { label: "Estimated cook time", value: `${fmt(cookHours, 1)} hrs`, sub: `${cut} at ${fmt(targetTemp)}°F`, accent: true },
          { label: "Charcoal needed", value: `${fmt(charcoalLbs, 1)} lb`, sub: `${smoker} smoker` },
          { label: "Wood chunks", value: fmt(woodChunks), sub: "Approx. count needed" },
          { label: "Fuel cost estimate", value: money(fuelCost), sub: `At ${money(price)}/lb` },
        ],
        table: {
          title: "Smoke session fuel breakdown",
          head: ["Metric", "Value"],
          rows: [
            ["Smoker type", smoker],
            ["Meat cut", cut],
            ["Meat weight", `${fmt(weight, 1)} lb`],
            ["Target temp", `${fmt(targetTemp)}°F`],
            ["Ambient temp", `${fmt(ambientTemp)}°F`],
            ["Cook time", `${fmt(cookHours, 1)} hrs`],
            ["Charcoal needed", `${fmt(charcoalLbs, 1)} lb`],
            ["Wood chunks", fmt(woodChunks)],
            ["Fuel cost", money(fuelCost)],
          ],
        },
        summary: `Plan for about ${fmt(cookHours, 1)} hours, ${fmt(charcoalLbs, 1)} lb of charcoal, and ${woodChunks} wood chunks.`,
      };
    },
    intro:
      "Running out of charcoal three hours into an overnight brisket smoke is a rite of passage every pitmaster wants to avoid. This calculator estimates total cook time from your meat cut and weight, then scales fuel consumption by smoker type, target temperature and cold-weather losses, so you can buy the right amount of charcoal and wood before you start.",
    sections: [
      { h: "Why smoker type changes fuel use so much", body: "Offset smokers lose heat constantly through the firebox and cooking chamber, requiring nearly continuous fuel feeding at roughly 2 lb per hour. Kettle grills set up for indirect cooking are more efficient at about 1 lb per hour, and insulated ceramic kamados sip fuel at around 0.5 lb per hour thanks to thick walls that retain heat." },
      { h: "Estimating cook time by cut", body: "Brisket and pork butt both follow a rough 1.5 hours per pound rule at 225-250°F, though both can stall for hours in the 150-170°F internal range. Ribs cook on a more fixed timeline regardless of weight, typically 4-6 hours, while chicken cooks much faster at roughly 45 minutes per pound." },
      { h: "How temperature and weather affect fuel burn", body: "Running below the standard 250°F target stretches cook time and therefore fuel use, since lower temperatures mean more total hours of burning. Cold ambient air below 50°F also forces the smoker to burn more fuel to maintain temperature, an effect this calculator scales up to a 50% increase at freezing conditions." },
      { h: "Wood chunks vs charcoal ratio", body: "As a starting point, plan roughly 1.5 wood chunks per hour of cook time for a well-smoked flavor without oversmoking, adjusting up for stronger woods like hickory in briskets or down for milder fruit woods with chicken and fish." },
    ],
    staticTable: {
      title: "Typical cook time and fuel by cut",
      head: ["Cut", "Weight", "Typical cook time @ 250°F", "Charcoal (offset)"],
      rows: [
        ["Brisket", "12 lb", "18 hrs", "36 lb"],
        ["Pork butt", "8 lb", "12 hrs", "24 lb"],
        ["Ribs (full rack)", "3 lb", "5 hrs", "10 lb"],
        ["Whole chicken", "5 lb", "3.75 hrs", "7.5 lb"],
        ["Turkey", "14 lb", "10.5 hrs", "21 lb"],
      ],
    },
    faqs: [
      { q: "How much charcoal do I need for a 12 lb brisket on an offset smoker?", a: "At roughly 1.5 hours per pound, a 12 lb brisket takes about 18 hours at 250°F, requiring roughly 36 lb of charcoal on an offset smoker burning at about 2 lb per hour." },
      { q: "Why does my kamado use so much less charcoal than my offset?", a: "Ceramic kamado grills are heavily insulated and retain heat far more efficiently, often using a quarter of the fuel an offset smoker burns for the same cook duration." },
      { q: "Does cooking at a lower temperature really use more fuel overall?", a: "Yes, because total cook time increases more than the hourly burn rate decreases, a longer low-and-slow cook at 225°F typically consumes more total fuel than the same cut cooked faster at 275°F." },
      { q: "How many wood chunks should I use for a pork butt?", a: "For a typical 12-hour pork butt cook, plan around 15-18 wood chunks, added gradually through the first half of the cook since meat stops absorbing significant smoke flavor once its surface sets." },
      { q: "Why does cold weather affect my fuel estimate?", a: "Below about 50°F ambient temperature, the smoker loses heat to the surrounding air faster, so it must burn more fuel per hour to hold the target internal temperature, an effect this calculator scales up proportionally with how cold it is." },
      { q: "Should I use lump charcoal or briquettes for these estimates?", a: "The pound-based estimates apply reasonably well to both, though lump charcoal burns hotter and faster while briquettes burn more consistently over time, so lump users may want to check fuel levels more frequently." },
      { q: "How long does a rack of ribs really take regardless of weight?", a: "Ribs are thin enough that weight has limited impact on cook time; most racks finish in 4-6 hours at 225-250°F using methods like 3-2-1, which is why this calculator uses a fixed baseline for ribs." },
      { q: "What's a realistic fuel cost for an all-day brisket smoke?", a: "At roughly 36 lb of charcoal and a typical price of $1-1.50 per pound, expect to spend $36-54 in charcoal alone for a full overnight brisket cook on an offset smoker, before wood cost." },
      { q: "Does this calculator account for the stall during long smokes?", a: "The hours-per-pound estimates for brisket and pork butt are averaged figures that already reflect typical stall behavior, but individual cooks can vary by several hours depending on humidity and wrapping technique." },
      { q: "Can I use this for a gas or pellet smoker instead?", a: "This tool is calibrated specifically for charcoal-based fuel consumption; pellet smokers consume wood pellets at a different rate (commonly 1-2 lb per hour) and gas smokers use propane, both requiring different calculations." },
    ],
    formula: {
      expressions: [
        "CookHours = HoursPerLb × Weight (or fixed for ribs)",
        "CookHours *= 1 + max(0, 250 − TargetTemp) × 0.004",
        "ColdFactor = 1 + max(0, 50 − Ambient) × 0.01",
        "CharcoalLbs = CookHours × SmokerFactor × ColdFactor",
      ],
      variables: [
        { name: "SmokerFactor", meaning: "Baseline lb/hr burn rate by smoker type (offset 2.0, kettle 1.0, kamado 0.5)" },
        { name: "ColdFactor", meaning: "Multiplier increasing fuel use in ambient temps below 50°F" },
      ],
    },
    related: ["chicken-coop-space-calculator", "concrete-bag-calculator", "lawn-fertilizer-calculator", "aquarium-stocking-calculator"],
  },

  {
    slug: "chicken-coop-space-calculator",
    cluster: "lifestyle",
    name: "Chicken Coop Space",
    h1: "Chicken Coop & Run Size Calculator: Square Feet Per Hen",
    title: "Chicken Coop & Run Size Calculator (2026)",
    description:
      "Calculate exactly how much coop and run space your flock needs instantly. Free online chicken coop size calculator with live formulas, unit conversion and a step-by-step breakdown.",
    keywords: [
      "how much coop space per chicken calculator",
      "chicken run size calculator",
      "nesting boxes per hen calculator",
      "roost bar length calculator chickens",
      "backyard chicken coop size guide",
      "bantam vs standard breed coop space",
    ],
    inputs: [
      { id: "hens", label: "Number of hens", type: "number", def: "6", min: 1, max: 500, step: 1 },
      { id: "breed", label: "Breed size", type: "select", def: "standard", options: [
        { value: "bantam", label: "Bantam (2 coop / 4 run sqft)" },
        { value: "standard", label: "Standard (4 coop / 10 run sqft)" },
        { value: "heavy", label: "Heavy (5 coop / 12 run sqft)" },
      ] },
      { id: "setup", label: "Setup type", type: "select", def: "coopandrun", options: [
        { value: "coopandrun", label: "Coop + run" },
        { value: "freerange", label: "Free range (coop only)" },
      ] },
    ],
    compute: (v) => {
      const hens = Math.max(1, Math.round(n(v, "hens", 1)));
      const breed = s(v, "breed", "standard");
      const setup = s(v, "setup", "coopandrun");

      const breedFactors: Record<string, { coop: number; run: number; roost: number }> = {
        bantam: { coop: 2, run: 4, roost: 8 },
        standard: { coop: 4, run: 10, roost: 10 },
        heavy: { coop: 5, run: 12, roost: 12 },
      };
      const f = breedFactors[breed] ?? breedFactors.standard;

      const coopSqFt = hens * f.coop;
      const runSqFt = setup === "freerange" ? 0 : hens * f.run;
      const roostInches = hens * f.roost;
      const nestingBoxes = Math.ceil(hens / 4);

      const coopSide = Math.sqrt(coopSqFt);
      const suggestedW = Math.max(2, Math.round(coopSide * 10) / 10);
      const suggestedL = coopSqFt > 0 ? Math.round((coopSqFt / suggestedW) * 10) / 10 : 0;

      return {
        cards: [
          { label: "Coop space needed", value: `${fmt(coopSqFt, 1)} sq ft`, sub: `${hens} hens, ${breed}`, accent: true },
          { label: "Run space needed", value: setup === "freerange" ? "N/A (free range)" : `${fmt(runSqFt, 1)} sq ft`, sub: setup === "freerange" ? "Coop only" : "Enclosed run" },
          { label: "Roost bar length", value: `${fmt(roostInches)} in`, sub: `${fmt(roostInches / 12, 1)} ft total` },
          { label: "Nesting boxes", value: fmt(nestingBoxes), sub: "1 box per 4 hens" },
        ],
        table: {
          title: "Coop & run planning breakdown",
          head: ["Metric", "Value"],
          rows: [
            ["Number of hens", fmt(hens)],
            ["Breed size", breed],
            ["Setup", setup === "freerange" ? "Free range (coop only)" : "Coop + run"],
            ["Coop sq ft", fmt(coopSqFt, 1)],
            ["Run sq ft", setup === "freerange" ? "N/A" : fmt(runSqFt, 1)],
            ["Roost bar (inches)", fmt(roostInches)],
            ["Nesting boxes", fmt(nestingBoxes)],
            ["Suggested coop dimensions", `${fmt(suggestedW, 1)} ft × ${fmt(suggestedL, 1)} ft`],
          ],
        },
        summary: `For ${hens} ${breed} hens, build about ${fmt(coopSqFt, 1)} sq ft of coop${setup === "freerange" ? "" : ` and ${fmt(runSqFt, 1)} sq ft of run`}, with ${nestingBoxes} nesting box(es).`,
      };
    },
    intro:
      "Cramped coops lead to feather-pecking, stress and disease, while an oversized run is wasted material and harder to predator-proof. This calculator applies standard poultry-keeping space guidelines by breed size to tell you exactly how much coop and run area, roost bar length and nesting boxes your flock needs.",
    sections: [
      { h: "Why breed size changes the numbers", body: "Bantam breeds weigh a fraction of standard or heavy breeds and need proportionally less space — roughly 2 sq ft of coop and 4 sq ft of run per bird. Standard breeds like Rhode Island Reds need about 4 sq ft coop and 10 sq ft run, while heavy dual-purpose breeds like Brahmas need the most room at 5 sq ft coop and 12 sq ft run." },
      { h: "Coop space vs run space", body: "Coop space is the enclosed, sheltered area where hens sleep, lay eggs and shelter from weather — this needs the least per-bird area since hens spend most active daylight hours outside. Run space is the fenced outdoor area for daytime activity and needs considerably more room per bird to prevent overcrowding, pecking order stress and mud buildup." },
      { h: "Roost bars and nesting boxes", body: "Each hen needs roughly 8-12 linear inches of roost bar depending on breed size, and bars should be positioned higher than nesting boxes so hens naturally prefer to sleep on the roost rather than in the boxes. One nesting box comfortably serves about four hens, since hens will happily queue and share boxes rather than needing one each." },
      { h: "Free range considerations", body: "If your flock free-ranges during the day, the enclosed run area becomes less critical since birds have full yard access, but coop space and roost/nesting requirements remain unchanged since those needs are driven by resting and laying behavior, not daytime activity." },
    ],
    staticTable: {
      title: "Space guidelines by breed size",
      head: ["Breed size", "Coop sq ft/hen", "Run sq ft/hen", "Roost inches/hen"],
      rows: [
        ["Bantam", "2", "4", "8"],
        ["Standard (e.g. Leghorn, Sussex)", "4", "10", "10"],
        ["Heavy (e.g. Brahma, Orpington)", "5", "12", "12"],
      ],
    },
    faqs: [
      { q: "How much coop space do I need for 6 standard hens?", a: "Six standard-breed hens need about 24 sq ft of coop space (4 sq ft each) and about 60 sq ft of run space (10 sq ft each) for comfortable, low-stress conditions." },
      { q: "Can I keep chickens with less space than recommended?", a: "You can temporarily, but overcrowding reliably increases feather-pecking, disease spread and stress-related egg-laying drops; treat these figures as minimums, not ideals, especially in cold climates where birds spend more time indoors." },
      { q: "How many nesting boxes do I really need?", a: "One box per four hens is standard since hens don't lay simultaneously and happily share or take turns, so six hens need two boxes and twelve hens need three." },
      { q: "Do roosters need extra roost or coop space?", a: "Roosters take up roughly the same space as a standard hen for roosting and coop purposes; count each rooster as one additional bird in your hen total for accurate space planning." },
      { q: "What's the minimum run size if I can't free range?", a: "If hens are permanently confined to a run without free-range time, err toward the higher end of the run space guideline or beyond, since confined birds need more room to prevent boredom-driven aggression." },
      { q: "How high should roost bars be positioned?", a: "Roost bars are typically mounted 2-4 feet off the coop floor and should sit higher than nesting boxes, since hens instinctively prefer the highest available perch to sleep, keeping them out of the boxes overnight." },
      { q: "Does coop space requirement change in winter?", a: "Birds spend far more time inside during cold or snowy weather, so coops sized at the minimum guideline can feel cramped in winter; sizing toward the higher end helps if your climate has long confinement periods." },
      { q: "How do I use the suggested coop dimensions?", a: "The tool suggests a roughly square footprint based on total square footage; adjust the length and width to fit your site while keeping the same total area, and ensure at least one dimension allows a human to comfortably enter for cleaning." },
      { q: "Is more roost bar length always better?", a: "Extra roost length beyond the guideline doesn't hurt, but each hen typically only uses her fair share nightly; prioritize meeting the minimum per-hen inches over adding excessive extra length that reduces usable coop floor space." },
      { q: "How much space do ducks or other poultry need compared to chickens?", a: "Ducks generally need less roost space (they don't roost) but more water-access area and slightly more floor space than chickens; this calculator is calibrated specifically for chicken breeds and not directly applicable to ducks or turkeys." },
    ],
    formula: {
      expressions: [
        "CoopSqFt = Hens × CoopFactor",
        "RunSqFt = Hens × RunFactor (0 if free range)",
        "RoostInches = Hens × RoostFactor",
        "NestingBoxes = ceil(Hens / 4)",
      ],
      variables: [
        { name: "CoopFactor", meaning: "Sq ft of coop per hen by breed size (bantam 2, standard 4, heavy 5)" },
        { name: "RunFactor", meaning: "Sq ft of run per hen by breed size (bantam 4, standard 10, heavy 12)" },
      ],
    },
    related: ["lawn-fertilizer-calculator", "concrete-bag-calculator", "aquarium-stocking-calculator", "bbq-charcoal-wood-calculator"],
  },
];
